import * as React from 'react';

import {Button} from 'shells/button/button';
import {colors} from 'shells/colors/colors';
import {MultiAddTextField} from 'shells/form/multi-add-textfield/multi-add-textfield';
import {AiSparkleNewIcon} from 'shells/icon/svg/ai-sparkle-new-icon';
import {Stack} from 'shells/layout/stack/stack';
import type {SaveState} from 'shells/saved-setting-toast/use-manage-save-state';
import {Body} from 'shells/typography/body';
import {LabelWithContent} from 'shells/typography/label-with-content';

import type {RecommendationsEnum} from 'nutshell-graphql-types';
import type {
    GetBehaviorSettings_chatSettings as BehaviorSettings,
    ChatSettingsInput,
} from 'nutshell-graphql-types';

import {AiResponseLoadingState} from '../ai-response-loading-state';
import {SAVE_STATE_KEYS} from '../consts';
import type {SaveStateKey} from '../consts';

type Props = {
    chatSettings: BehaviorSettings;
    savingToastKey: SaveStateKey | null | undefined;
    toastState: SaveState;
    handleSave: (updates: ChatSettingsInput, toastKey: SaveStateKey) => void;
    handleGenerateRecommendations: (type: RecommendationsEnum) => void;
    isGeneratingRecommendations: {
        SUGGESTIONS: boolean;
        TOPICS_TO_AVOID: boolean;
    };
    isDisabled: boolean;
};

const LIST_SAVE_DELAY = 800;

export function GuidanceSection(props: Props) {
    const [suggestions, setSuggestions] = React.useState(() => {
        const mapped = props.chatSettings.chatbot.suggestions.map((suggestion) => suggestion || '');

        return mapped.length > 0 ? mapped : [''];
    });
    const [topicsToAvoid, setTopicsToAvoid] = React.useState(() => {
        const mapped = props.chatSettings.chatbot.topicsToAvoid.map((topic) => topic || '');

        return mapped.length > 0 ? mapped : [''];
    });

    React.useEffect(() => {
        setSuggestions(
            props.chatSettings.chatbot.suggestions.map((suggestion) => suggestion || '')
        );
        setTopicsToAvoid(props.chatSettings.chatbot.topicsToAvoid.map((topic) => topic || ''));
    }, [props.chatSettings.chatbot.suggestions, props.chatSettings.chatbot.topicsToAvoid]);

    return (
        <Stack spacing={16}>
            <Body size='large' isBold={true}>
                Guidance
            </Body>
            <div className={props.isDisabled ? 'disabled' : ''}>
                <LabelWithContent
                    label='Topics to Promote'
                    labelPopoverProps={{
                        info: 'Give the bot additional guidance on how to respond. For example: "Encourage visitors to call our main phone number" or "We offer a 10% discount for purchases in August"',
                        bodyColor: 'dark',
                    }}
                    labelButton={
                        !props.isGeneratingRecommendations.SUGGESTIONS &&
                        !props.isGeneratingRecommendations.TOPICS_TO_AVOID && (
                            <Button
                                variant='text'
                                size='small'
                                disabled={props.isDisabled}
                                onClick={() => props.handleGenerateRecommendations('SUGGESTIONS')}
                            >
                                {/* @ts-expect-error */}
                                <Stack spacing={4} isHorizontal={true} alignCenter={true}>
                                    <AiSparkleNewIcon
                                        size={13}
                                        // @ts-expect-error
                                        fill={colors.plum}
                                        alignCenter={true}
                                    />
                                    <Body color='plum' size='tiny'>
                                        Generate suggestions
                                    </Body>
                                </Stack>
                            </Button>
                        )
                    }
                    state={
                        props.savingToastKey === SAVE_STATE_KEYS.SUGGESTIONS
                            ? props.toastState
                            : 'initial'
                    }
                    spacing={4}
                    content={
                        props.isGeneratingRecommendations.SUGGESTIONS ? (
                            <AiResponseLoadingState />
                        ) : (
                            <MultiAddTextField
                                items={suggestions}
                                onChange={(newSuggestions) => {
                                    if (props.isDisabled) {
                                        return;
                                    }

                                    setSuggestions(newSuggestions);
                                    props.handleSave(
                                        {
                                            chatbot: {
                                                suggestions: newSuggestions
                                                    .map((item) => item)
                                                    .filter((s) => s && s.trim() !== ''),
                                            },
                                        },
                                        SAVE_STATE_KEYS.SUGGESTIONS
                                    );
                                }}
                                itemConfig={{
                                    getValue: (item) => item,
                                    setValue: (item, value) => value,
                                    createNew: () => '',
                                    getKey: (item, index) => `suggestion-${index}`,
                                }}
                                isDebouncedSave={true}
                                debouncedSaveDelay={LIST_SAVE_DELAY}
                                placeholder='Enter a suggestion'
                                addButtonText='Add suggestion'
                                minItems={1}
                                maxItems={5}
                                isDisabled={props.isDisabled}
                                textFieldProps={{
                                    multiline: true,
                                    shouldGrowVertically: true,
                                }}
                            />
                        )
                    }
                />
            </div>
            <div className={props.isDisabled ? 'disabled' : ''}>
                <LabelWithContent
                    label='Topics to Avoid'
                    labelPopoverProps={{
                        info: 'Give the bot additional guidance on topics to avoid. For instance: "Avoid engaging in sensitive matters such as personal health or political opinions."',
                        bodyColor: 'dark',
                    }}
                    labelButton={
                        !props.isGeneratingRecommendations.TOPICS_TO_AVOID &&
                        !props.isGeneratingRecommendations.SUGGESTIONS && (
                            <Button
                                variant='text'
                                size='small'
                                disabled={props.isDisabled}
                                onClick={() =>
                                    props.handleGenerateRecommendations('TOPICS_TO_AVOID')
                                }
                            >
                                {/* @ts-expect-error */}
                                <Stack spacing={4} isHorizontal={true} alignCenter={true}>
                                    <AiSparkleNewIcon
                                        size={13}
                                        // @ts-expect-error
                                        fill={colors.plum}
                                        alignCenter={true}
                                    />
                                    <Body color='plum' size='tiny'>
                                        Generate topics
                                    </Body>
                                </Stack>
                            </Button>
                        )
                    }
                    state={
                        props.savingToastKey === SAVE_STATE_KEYS.TOPICS_TO_AVOID
                            ? props.toastState
                            : 'initial'
                    }
                    spacing={4}
                    content={
                        props.isGeneratingRecommendations.TOPICS_TO_AVOID ? (
                            <AiResponseLoadingState />
                        ) : (
                            <MultiAddTextField
                                items={topicsToAvoid}
                                onChange={(newTopicsToAvoid) => {
                                    if (props.isDisabled) {
                                        return;
                                    }

                                    setTopicsToAvoid(newTopicsToAvoid);
                                    props.handleSave(
                                        {
                                            chatbot: {
                                                topicsToAvoid: newTopicsToAvoid
                                                    .map((item) => item)
                                                    .filter((t) => t && t.trim() !== ''),
                                            },
                                        },
                                        SAVE_STATE_KEYS.TOPICS_TO_AVOID
                                    );
                                }}
                                itemConfig={{
                                    getValue: (item) => item,
                                    setValue: (item, value) => value,
                                    createNew: () => '',
                                    getKey: (item, index) => `topic-${index}`,
                                }}
                                isDebouncedSave={true}
                                debouncedSaveDelay={LIST_SAVE_DELAY}
                                placeholder='Enter a topic to avoid'
                                addButtonText='Add topic'
                                minItems={1}
                                maxItems={5}
                                isDisabled={props.isDisabled}
                                textFieldProps={{
                                    multiline: true,
                                    shouldGrowVertically: true,
                                }}
                            />
                        )
                    }
                />
            </div>
        </Stack>
    );
}
